import { sortBy } from "lodash"
import type { AgendaTypes } from "../../../utils/types/pagesTypes"
import { AgendaTypesEnum } from "../../../utils/types/pagesTypes"
import type {
    FreeCountedOrderItem,
    GroupItemPriceData,
    JoinedGroupsPrice,
    JoinedItemsPrice,
    OrderItem,
    OrderItemCategoryGroup,
    OrderItemPriceData,
    OrderItemSpecialPricing,
} from "./orders/types"

export const DEFAULT_TIMEZONE = "America/New_York"

export const REPORTS_MAX_RANGE_DAYS = 93 // 3 months for all reports (except DSR)

export const mapAgendaNameToApiRoute = (agendaName: AgendaTypes): string =>
    ({
        transactionsReport: "sales-transactions/v2/csv",
        cash: "v2/cash-register/csv",
        customers: "customers/csv",
        customersFeedback: "customer-feedback/csv",
        orders: "orders/csv",
        summaryReport: "v2/daily-summary/csv-v2",
        [AgendaTypesEnum.SalesByItems]: "sales-by-items/csv",
        [AgendaTypesEnum.SalesByModifiers]: "sales-by-modifiers/csv",
        [AgendaTypesEnum.SalesByModifierGroups]: "sales-by-modifier-groups/csv",
    })[agendaName]
// Todo: map all agendas & move this function somewhere

export const countFreeItemsInCategoryGroup = (
    orderItems: OrderItem[],
    parentOrderItemId: string,
    group: OrderItemCategoryGroup
): FreeCountedOrderItem[] => {
    const groupOrderItems = orderItems.filter(
        oi =>
            oi.parentOrderItemId === parentOrderItemId &&
            oi.orderItemCategoryGroupId === group.id &&
            oi.categoryGroupItemId != null
    )
    const sortedGroupOrderItems = sortBy(groupOrderItems, [oi => -(oi.price - oi.supplement), oi => oi.originTimestamp])
    // How many from the item quantity are for free?
    return sortedGroupOrderItems.reduce(
        ({ remainingFree, items }, orderItem) => ({
            remainingFree: Math.max(remainingFree - orderItem.quantity, 0),
            items: [
                ...items,
                {
                    orderItem: orderItem,
                    free: Math.min(remainingFree, orderItem.quantity),
                },
            ],
        }),
        {
            remainingFree: group.free || 0,
            items: [],
        }
    ).items
}

export const computeGroupOrderItemPrices = (
    orderItems: OrderItem[],
    parentOrderItemId: string,
    group: OrderItemCategoryGroup
): OrderItemPriceData[] => {
    const freeCounts = countFreeItemsInCategoryGroup(orderItems, parentOrderItemId, group)
    return freeCounts.map(({ orderItem, free }) => {
        const supplementsTotal = free * orderItem.supplement
        const unitPricesTotal = (orderItem.quantity - free) * orderItem.price
        return {
            orderItemId: orderItem.id,
            totalPrice: supplementsTotal + unitPricesTotal,
            free: free,
            freeTotal: supplementsTotal,
            extraTotal: unitPricesTotal,
        }
    })
}

export const computeGroupsOrderItemPrices = (
    orderItems: OrderItem[],
    parentOrderItemId: string,
    groups: OrderItemCategoryGroup[]
): GroupItemPriceData[] => {
    const grouppedItems = groups.map(group => ({
        orderItemCategoryGroupId: group.id,
        itemPrices: computeGroupOrderItemPrices(orderItems, parentOrderItemId, group),
        groupPrice: group.group_price,
    }))
    const outOfGroupItemPrices = orderItems
        .filter(oi => oi.parentOrderItemId === parentOrderItemId)
        .filter(
            oi =>
                oi.orderItemCategoryGroupId == null || // TODO: out of group items should have null cat. gr. ID
                oi.categoryGroupItemId == null
        )
        .map(oi => ({
            orderItemId: oi.id,
            totalPrice: oi.price,
            free: 0,
            freeTotal: 0,
            extraTotal: oi.price,
        }))
    return [
        ...grouppedItems,
        {
            orderItemCategoryGroupId: null,
            itemPrices: outOfGroupItemPrices,
            groupPrice: null,
        },
    ]
}

export const joinGroupsPrices = (
    items: OrderItem[],
    itemCategoryGroups: OrderItemCategoryGroup[],
    groupsPrices: GroupItemPriceData[]
): JoinedGroupsPrice[] =>
    groupsPrices.map(gp => ({
        groupPrice: gp.groupPrice,
        group: itemCategoryGroups.find(icg => icg.id === gp.orderItemCategoryGroupId),
        itemPrices: gp.itemPrices.map(({ orderItemId, totalPrice, free, freeTotal, extraTotal }) => ({
            totalPrice: totalPrice,
            free: free,
            freeTotal: freeTotal,
            extraTotal: extraTotal,
            item: items.find(i => i.id === orderItemId),
        })),
    }))

export const multiplyItemsToFreeAndExtra = (items: JoinedItemsPrice[]) => {
    const sorted = sortBy(items, i => i.item && i.item.originTimestamp)
    const free = sorted.map(i => ({
        item: i.item,
        price: i.freeTotal,
        quantity: i.free,
        key: (i.item && i.item.id ? i.item.id : "") + "-free",
    }))
    const extra = sorted.map(i => ({
        item: i.item,
        price: i.extraTotal,
        quantity: (i.item && i.item.quantity - i.free) || 0,
        key: (i.item && i.item.id ? i.item.id : "") + "-extra",
    }))
    return [...free, ...extra].filter(i => i.quantity > 0)
}

export const pricingAppliesToAllItems = (
    pricings: OrderItemSpecialPricing[],
    orderItems: OrderItem[],
    pricing: OrderItemSpecialPricing
): boolean => {
    const pricingsCount = pricings.filter(
        sp => sp.special_pricing_id === pricing.special_pricing_id && sp.is_active
    ).length
    return pricingsCount === orderItems.length
}
